/**
 * Homeofficelife Wishlist System
 * Stores and manages favorite products in localStorage with instant real-time UI sync.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'hol_wishlist_products';

  class WishlistManager {
    constructor() {
      this.items = this.loadFromStorage();
      this.init();
    }

    init() {
      // Listen for storage changes across tabs
      window.addEventListener('storage', (e) => {
        if (e.key === STORAGE_KEY) {
          this.items = this.loadFromStorage();
          this.updateUI();
          this.renderDrawer();
          this.renderWishlistPage();
        }
      });

      // DOM Ready setup
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupDOM());
      } else {
        this.setupDOM();
      }
    }

    setupDOM() {
      this.updateUI();
      this.renderDrawer();
      this.renderWishlistPage();
      this.bindEvents();
    }

    loadFromStorage() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (err) {
        console.warn('Wishlist: Could not load from localStorage', err);
        return [];
      }
    }

    saveToStorage() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (err) {
        console.warn('Wishlist: Could not save to localStorage', err);
      }
    }

    getItems() {
      return [...this.items];
    }

    getCount() {
      return this.items.length;
    }

    has(idOrHandle) {
      if (!idOrHandle) return false;
      const strId = String(idOrHandle);
      return this.items.some((item) => String(item.id) === strId || String(item.handle) === strId);
    }

    add(product) {
      if (!product || !product.id) return false;
      const strId = String(product.id);
      if (this.has(strId)) return false;

      const item = {
        id: strId,
        variantId: product.variantId ? String(product.variantId) : '',
        title: product.title || '',
        handle: product.handle || '',
        price: product.price || '',
        compareAtPrice: product.compareAtPrice || '',
        image: product.image || '',
        url: product.url || '',
        vendor: product.vendor || '',
        available: product.available !== false,
        addedAt: Date.now()
      };

      this.items.unshift(item);
      this.saveToStorage();
      this.dispatchChange('added', item);
      this.showToast(`"${item.title}" zu Favoriten hinzugefügt`, 'success');
      return true;
    }

    remove(idOrHandle) {
      if (!idOrHandle) return false;
      const strId = String(idOrHandle);
      const index = this.items.findIndex(
        (item) => String(item.id) === strId || String(item.handle) === strId
      );

      if (index === -1) return false;

      const removed = this.items.splice(index, 1)[0];
      this.saveToStorage();
      this.dispatchChange('removed', removed);
      this.showToast(`"${removed.title}" aus Favoriten entfernt`, 'info');
      return true;
    }

    toggle(product) {
      if (!product || !product.id) return false;
      const strId = String(product.id);
      if (this.has(strId)) {
        this.remove(strId);
        return false;
      } else {
        this.add(product);
        return true;
      }
    }

    clear() {
      if (this.items.length === 0) return;
      this.items = [];
      this.saveToStorage();
      this.dispatchChange('cleared', null);
      this.showToast('Favoritenliste wurde geleert', 'info');
    }

    dispatchChange(action, item) {
      this.updateUI();
      this.renderDrawer();
      this.renderWishlistPage();

      const event = new CustomEvent('wishlist:updated', {
        bubbles: true,
        detail: {
          action,
          item,
          count: this.getCount(),
          items: this.getItems()
        }
      });
      document.dispatchEvent(event);
    }

    updateUI() {
      const count = this.getCount();

      // Update all count badges in header, drawer, etc.
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = count;
        if (count > 0) {
          el.classList.remove('is-hidden');
          el.removeAttribute('hidden');
          el.setAttribute('data-has-items', 'true');
        } else {
          el.classList.add('is-hidden');
          el.setAttribute('hidden', '');
          el.setAttribute('data-has-items', 'false');
        }
      });

      // Update all toggle buttons in the page
      document.querySelectorAll('[data-wishlist-button]').forEach((btn) => {
        const id = btn.getAttribute('data-product-id');
        const handle = btn.getAttribute('data-product-handle');
        const isFav = this.has(id) || (handle && this.has(handle));

        if (isFav) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
          const labelEl = btn.querySelector('.wishlist-button-text');
          if (labelEl) labelEl.textContent = 'Auf der Wunschliste';
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
          const labelEl = btn.querySelector('.wishlist-button-text');
          if (labelEl) labelEl.textContent = 'Zu Favoriten';
        }
      });
    }

    bindEvents() {
      // Delegate click on wishlist toggle buttons
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-button]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();

          const productData = {
            id: btn.getAttribute('data-product-id'),
            variantId: btn.getAttribute('data-product-variant-id') || '',
            title: btn.getAttribute('data-product-title') || 'Produkt',
            handle: btn.getAttribute('data-product-handle') || '',
            price: btn.getAttribute('data-product-price') || '',
            compareAtPrice: btn.getAttribute('data-product-compare-at') || '',
            image: btn.getAttribute('data-product-image') || '',
            url: btn.getAttribute('data-product-url') || '',
            vendor: btn.getAttribute('data-product-vendor') || '',
            available: btn.getAttribute('data-product-available') !== 'false'
          };

          this.toggle(productData);

          // Add a subtle pop animation to the clicked button
          btn.classList.add('pop-anim');
          setTimeout(() => btn.classList.remove('pop-anim'), 400);
          return;
        }

        // Drawer open trigger
        const drawerOpen = e.target.closest('[data-wishlist-drawer-open]');
        if (drawerOpen) {
          e.preventDefault();
          this.openDrawer();
          return;
        }

        // Drawer close trigger
        const drawerClose = e.target.closest('[data-wishlist-drawer-close]');
        if (drawerClose) {
          e.preventDefault();
          this.closeDrawer();
          return;
        }

        // Remove item from drawer or page
        const removeBtn = e.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          e.preventDefault();
          const id = removeBtn.getAttribute('data-wishlist-remove');
          this.remove(id);
          return;
        }

        // Add to cart from wishlist
        const addToCartBtn = e.target.closest('[data-wishlist-add-to-cart]');
        if (addToCartBtn) {
          e.preventDefault();
          const variantId = addToCartBtn.getAttribute('data-variant-id');
          if (variantId) {
            this.addToCart(variantId, addToCartBtn);
          }
          return;
        }

        // Clear all wishlist items
        const clearBtn = e.target.closest('[data-wishlist-clear-all]');
        if (clearBtn) {
          e.preventDefault();
          if (confirm('Möchtest du wirklich alle Favoriten löschen?')) {
            this.clear();
          }
          return;
        }
      });

      // Close drawer on ESC
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDrawer();
        }
      });
    }

    openDrawer() {
      const drawer = document.getElementById('hol-wishlist-drawer');
      if (!drawer) return;
      this.renderDrawer();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('hol-wishlist-drawer-open');
    }

    closeDrawer() {
      const drawer = document.getElementById('hol-wishlist-drawer');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('hol-wishlist-drawer-open');
    }

    renderDrawer() {
      const listContainer = document.getElementById('hol-wishlist-drawer-items');
      const emptyContainer = document.getElementById('hol-wishlist-drawer-empty');
      const footer = document.getElementById('hol-wishlist-drawer-footer');
      if (!listContainer) return;

      const items = this.getItems();

      if (items.length === 0) {
        listContainer.innerHTML = '';
        listContainer.style.display = 'none';
        if (emptyContainer) emptyContainer.style.display = 'flex';
        if (footer) footer.style.display = 'none';
        return;
      }

      if (emptyContainer) emptyContainer.style.display = 'none';
      if (footer) footer.style.display = 'block';
      listContainer.style.display = 'flex';

      listContainer.innerHTML = items
        .map(
          (item) => `
        <div class="hol-wishlist-item" data-id="${item.id}">
          <a href="${item.url || '#'}" class="hol-wishlist-item__img-link">
            ${
              item.image
                ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy" width="80" height="80">`
                : `<div class="hol-wishlist-item__no-img">Kein Bild</div>`
            }
          </a>
          <div class="hol-wishlist-item__details">
            ${item.vendor ? `<span class="hol-wishlist-item__vendor">${this.escapeHtml(item.vendor)}</span>` : ''}
            <a href="${item.url || '#'}" class="hol-wishlist-item__title">${this.escapeHtml(item.title)}</a>
            <div class="hol-wishlist-item__price">${item.price}</div>
            
            <div class="hol-wishlist-item__actions">
              ${
                item.variantId && item.available
                  ? `<button type="button" class="hol-wishlist-btn-cart" data-wishlist-add-to-cart data-variant-id="${item.variantId}">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                      In den Warenkorb
                    </button>`
                  : `<a href="${item.url || '#'}" class="hol-wishlist-btn-cart">Zum Produkt</a>`
              }
              <button type="button" class="hol-wishlist-btn-remove" data-wishlist-remove="${item.id}" aria-label="Aus Favoriten entfernen">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                Löschen
              </button>
            </div>
          </div>
        </div>
      `
        )
        .join('');
    }

    renderWishlistPage() {
      const pageGrid = document.getElementById('hol-wishlist-page-grid');
      const pageEmpty = document.getElementById('hol-wishlist-page-empty');
      const pageCount = document.getElementById('hol-wishlist-page-count');
      if (!pageGrid) return;

      const items = this.getItems();

      if (pageCount) {
        pageCount.textContent = `(${items.length})`;
      }

      if (items.length === 0) {
        pageGrid.innerHTML = '';
        pageGrid.style.display = 'none';
        if (pageEmpty) pageEmpty.style.display = 'block';
        return;
      }

      if (pageEmpty) pageEmpty.style.display = 'none';
      pageGrid.style.display = 'grid';

      pageGrid.innerHTML = items
        .map(
          (item) => `
        <div class="hol-wishlist-page-card" data-id="${item.id}">
          <div class="hol-wishlist-page-card__img-wrap">
            <button type="button" class="hol-wishlist-page-card__remove" data-wishlist-remove="${item.id}" title="Aus Favoriten entfernen" aria-label="Entfernen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <a href="${item.url || '#'}" class="hol-wishlist-page-card__img-link">
              ${
                item.image
                  ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
                  : `<div class="hol-wishlist-page-card__no-img">Kein Bild</div>`
              }
            </a>
          </div>
          <div class="hol-wishlist-page-card__body">
            ${item.vendor ? `<span class="hol-wishlist-page-card__vendor">${this.escapeHtml(item.vendor)}</span>` : ''}
            <a href="${item.url || '#'}" class="hol-wishlist-page-card__title">${this.escapeHtml(item.title)}</a>
            <div class="hol-wishlist-page-card__price">${item.price}</div>
            
            <div class="hol-wishlist-page-card__actions">
              ${
                item.variantId && item.available
                  ? `<button type="button" class="hol-wishlist-page-card__add-btn" data-wishlist-add-to-cart data-variant-id="${item.variantId}">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                      In den Warenkorb
                    </button>`
                  : `<a href="${item.url || '#'}" class="hol-wishlist-page-card__add-btn">Optionen wählen</a>`
              }
            </div>
          </div>
        </div>
      `
        )
        .join('');
    }

    async addToCart(variantId, buttonEl) {
      if (!variantId) return;

      const origText = buttonEl ? buttonEl.innerHTML : '';
      if (buttonEl) {
        buttonEl.disabled = true;
        buttonEl.innerHTML = `
          <svg class="hol-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>
          Hinzufügen...
        `;
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }]
          })
        });

        if (res.ok) {
          this.showToast('Erfolgreich zum Warenkorb hinzugefügt!', 'success');
          // Close wishlist drawer and open cart drawer if available
          this.closeDrawer();

          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }

          // Trigger general cart update event
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } else {
          const data = await res.json();
          this.showToast(data.description || 'Konnte nicht hinzugefügt werden', 'error');
        }
      } catch (err) {
        console.error('Error adding to cart', err);
        this.showToast('Fehler beim Hinzufügen zum Warenkorb', 'error');
      } finally {
        if (buttonEl) {
          buttonEl.disabled = false;
          buttonEl.innerHTML = origText;
        }
      }
    }

    showToast(message, type = 'info') {
      let toast = document.getElementById('hol-wishlist-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'hol-wishlist-toast';
        toast.className = 'hol-wishlist-toast';
        document.body.appendChild(toast);
      }

      toast.textContent = message;
      toast.className = `hol-wishlist-toast hol-wishlist-toast--${type} is-visible`;

      clearTimeout(this._toastTimeout);
      this._toastTimeout = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 2800);
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  window.Wishlist = new WishlistManager();
})();
